import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { useTransactions } from '../context/TransactionContext';
import { useToast } from '../context/ToastContext';

function Settings() {
  const { user, updateProfile, logout, loading: authLoading } = useAuth();
  const { transactions, addTransaction } = useTransactions();
  const toast = useToast();
  const { 
    preferences, 
    categories, 
    loading, 
    updatePreferences, 
    updateCategories, 
    changePassword,
    clearData,
    deleteAccount,
    formatDate
  } = useSettings();
  
  const [activeTab, setActiveTab] = useState('profile');
  const [localProfile, setLocalProfile] = useState(user || {});
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Update local state when context changes
  React.useEffect(() => {
    setLocalProfile(user || {});
  }, [user]);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);

  const countryCodes = [
    { code: '+91', country: 'IN', flag: 'IN' },
    { code: '+1', country: 'US', flag: 'US' },
    { code: '+44', country: 'UK', flag: 'UK' },
    { code: '+49', country: 'DE', flag: 'DE' },
    { code: '+33', country: 'FR', flag: 'FR' },
    { code: '+86', country: 'CN', flag: 'CN' },
    { code: '+81', country: 'JP', flag: 'JP' },
    { code: '+61', country: 'AU', flag: 'AU' },
    { code: '+1', country: 'CA', flag: 'CA' },
    { code: '+55', country: 'BR', flag: 'BR' }
  ];

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [showClearDataConfirm, setShowClearDataConfirm] = useState(false);
  const [pendingImportFile, setPendingImportFile] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [clearDataConfirmText, setClearDataConfirmText] = useState('');
  const [clearDataPassword, setClearDataPassword] = useState('');
  const [newIncomeCategory, setNewIncomeCategory] = useState('');
  const [newExpenseCategory, setNewExpenseCategory] = useState('');

  const predefinedIncomeCategories = ['Salary', 'Business', 'Freelance', 'Investment', 'Rental Income', 'Bonus', 'Commission', 'Dividend', 'Interest', 'Gift', 'Other Income'];
  const predefinedExpenseCategories = ['Food & Dining', 'Transportation', 'Shopping', 'Entertainment', 'Bills & Utilities', 'Healthcare', 'Education', 'Travel', 'Insurance', 'Rent', 'Groceries', 'Gas', 'Other Expense'];
  const [isEditing, setIsEditing] = useState(false);

  const tabs = [
    { 
      id: 'profile', 
      label: 'Profile', 
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
    },
    { 
      id: 'preferences', 
      label: 'Preferences', 
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/></svg>
    },
    { 
      id: 'categories', 
      label: 'Categories', 
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
    },
    { 
      id: 'security', 
      label: 'Security', 
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M18,8h-1V6c0-2.76-2.24-5-5-5S7,3.24,7,6v2H6c-1.1,0-2,0.9-2,2v10c0,1.1,0.9,2,2,2h12c1.1,0,2-0.9,2-2V10 C20,8.9,19.1,8,18,8z M12,17c-1.1,0-2-0.9-2-2s0.9-2,2-2s2,0.9,2,2S13.1,17,12,17z M15.1,8H8.9V6c0-1.71,1.39-3.1,3.1-3.1 s3.1,1.39,3.1,3.1V8z"/></svg>
    },
    { 
      id: 'account', 
      label: 'Account', 
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M3 5v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2H5c-1.11 0-2 .9-2 2zm12 4c0 1.66-1.34 3-3 3s-3-1.34-3-3 1.34-3 3-3 3 1.34 3 3zm-9 8c0-2 4-3.1 6-3.1s6 1.1 6 3.1v1H6v-1z"/></svg>
    }
  ];

  const handleProfileSave = async () => {
    try {
      await updateProfile(localProfile);
      setIsEditing(false);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Failed to save profile:', error);
      toast.error('Failed to update profile');
    }
  };



  const formatDateForInput = (dateString) => {
    if (!dateString || dateString === 'Not specified' || dateString === 'Invalid date') return '';
    try {
      // Handle DD/MM/YYYY format
      if (dateString.includes('/')) {
        const [day, month, year] = dateString.split('/');
        if (year && month && day) {
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      }
      // Handle ISO date format (YYYY-MM-DD)
      if (dateString.includes('-') && dateString.length === 10) {
        return dateString;
      }
      // Try to parse as a date and convert to YYYY-MM-DD
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
      return '';
    } catch (error) {
      console.warn('Invalid date format:', dateString);
      return '';
    }
  };

  const formatPhoneNumber = (value, countryCode) => {
    // Remove all non-numeric characters
    const cleaned = value.replace(/\D/g, '');
    
    if (countryCode === '+91') {
      // Indian phone number formatting
      if (cleaned.length <= 5) return cleaned;
      if (cleaned.length <= 8) return `${cleaned.slice(0, 5)}-${cleaned.slice(5)}`;
      return `${cleaned.slice(0, 5)}-${cleaned.slice(5, 8)}-${cleaned.slice(8, 10)}`;
    }
    
    // Default formatting for other countries
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    if (cleaned.length <= 10) return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    const countryCode = localProfile?.countryCode || '+91';
    const formatted = formatPhoneNumber(value, countryCode);
    setLocalProfile(prev => ({ ...prev, phone: formatted }));
  };

  const validatePhoneNumber = (phone, countryCode) => {
    if (!phone) return true; // Empty is valid
    const cleaned = phone.replace(/\D/g, '');
    
    if (countryCode === '+91') {
      // Indian mobile numbers: 10 digits, starting with 6-9
      return cleaned.length === 10 && /^[6-9]/.test(cleaned);
    }
    
    // Basic validation for other countries (7-15 digits)
    return cleaned.length >= 7 && cleaned.length <= 15;
  };

  const handlePreferenceChange = async (key, value) => {
    try {
      await updatePreferences({ [key]: value });
      toast.success('Settings saved successfully!');
    } catch (error) {
      console.error('Failed to update preference:', error);
      toast.error('Failed to save settings');
    }
  };

  const resizeImage = (file, maxWidth = 150, quality = 0.7) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        const size = Math.min(img.width, img.height);
        canvas.width = maxWidth;
        canvas.height = maxWidth;
        
        ctx.drawImage(
          img,
          (img.width - size) / 2,
          (img.height - size) / 2,
          size,
          size,
          0,
          0,
          maxWidth,
          maxWidth
        );
        
        canvas.toBlob(resolve, 'image/jpeg', quality);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleProfilePictureChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image file');
      return;
    }
    
    try {
      const resizedBlob = await resizeImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicturePreview(reader.result);
        setLocalProfile(prev => ({ ...prev, profilePicture: reader.result }));
        toast.success('Profile picture updated!');
      };
      reader.readAsDataURL(resizedBlob);
    } catch (error) {
      toast.error('Failed to process image');
    }
  };

  const addIncomeCategory = async (category) => {
    if (category && !categories.income.includes(category)) {
      const newCategories = { ...categories, income: [...categories.income, category] };
      try {
        await updateCategories(newCategories);
        setNewIncomeCategory('');
      } catch (error) {
        console.error('Failed to add category:', error);
      }
    }
  };

  const addExpenseCategory = async (category) => {
    if (category && !categories.expense.includes(category)) {
      const newCategories = { ...categories, expense: [...categories.expense, category] };
      try {
        await updateCategories(newCategories);
        setNewExpenseCategory('');
      } catch (error) {
        console.error('Failed to add category:', error);
      }
    }
  };

  const removeCategory = async (type, categoryToRemove) => {
    const newCategories = {
      ...categories,
      [type]: categories[type].filter(cat => cat !== categoryToRemove)
    };
    try {
      await updateCategories(newCategories);
    } catch (error) {
      console.error('Failed to remove category:', error);
    }
  };

  const exportData = () => {
    const data = {
      profile: user,
      preferences,
      categories,
      transactions,
      exportDate: new Date().toISOString()
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `finance-data-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setShowExportConfirm(false);
  };

  const handleImportFile = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setPendingImportFile(file);
    setShowImportConfirm(true);
    event.target.value = '';
  };

  const importData = () => {
    if (!pendingImportFile) return;
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const fileContent = e.target.result;
        const fileName = pendingImportFile.name.toLowerCase();
        
        if (fileName.endsWith('.json')) {
          // Handle JSON import
          const data = JSON.parse(fileContent);
          
          if (data.profile) await updateProfile(data.profile);
          if (data.preferences) await updatePreferences(data.preferences);
          if (data.categories) await updateCategories(data.categories);
          
          toast.success('JSON data imported successfully!');
        } else if (fileName.endsWith('.csv')) {
          // Handle CSV import
          const lines = fileContent.split('\n');
          const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
          
          // Validate CSV format
          const requiredHeaders = ['date', 'description', 'amount', 'category'];
          const hasValidHeaders = requiredHeaders.some(header => 
            headers.includes(header) || headers.includes(header.replace(/['"]/g, ''))
          );
          
          if (!hasValidHeaders) {
            toast.error('Invalid CSV format. Required columns: Date, Description, Amount, Category');
            return;
          }
          
          // Parse CSV transactions
          const transactions = [];
          for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue;
            
            const values = line.split(',').map(v => v.replace(/['"]/g, '').trim());
            
            if (values.length >= 4) {
              const dateIndex = headers.findIndex(h => h.includes('date'));
              const descIndex = headers.findIndex(h => h.includes('description'));
              const amountIndex = headers.findIndex(h => h.includes('amount'));
              const categoryIndex = headers.findIndex(h => h.includes('category'));
              
              if (dateIndex >= 0 && descIndex >= 0 && amountIndex >= 0 && categoryIndex >= 0) {
                const amount = parseFloat(values[amountIndex]);
                if (!isNaN(amount)) {
                  transactions.push({
                    date: values[dateIndex],
                    description: values[descIndex] || 'Imported transaction',
                    amount: amount,
                    category: values[categoryIndex] || 'Other',
                    type: amount < 0 ? 'expense' : 'income'
                  });
                }
              }
            }
          }
          
          if (transactions.length === 0) {
            toast.error('No valid transactions found in CSV file');
            return;
          }
          
          // Import transactions using the transaction context
          let importCount = 0;
          for (const transaction of transactions) {
            try {
              const result = await addTransaction(transaction);
              if (result.success) importCount++;
            } catch (error) {
              console.error('Failed to import transaction:', error);
            }
          }
          
          toast.success(`CSV data imported successfully! ${importCount} transactions added.`);
        } else {
          toast.error('Unsupported file format. Please select a JSON or CSV file.');
        }
      } catch (error) {
        console.error('Import error:', error);
        toast.error('Failed to import data. Please check the file format.');
      }
    };
    reader.readAsText(pendingImportFile);
    setShowImportConfirm(false);
    setPendingImportFile(null);
  };

  // Show loading while user data is being fetched
  if (authLoading || !user) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '400px',
        fontSize: '1.125rem',
        color: 'var(--gray-500)'
      }}>
        Loading profile data...
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
          Settings
        </h1>
        <p style={{ color: 'var(--gray-500)' }}>
          Manage your account and preferences
        </p>
      </div>

  {/* Top Tabs - AI Assistant Style */}
  <div className="settings-tabs" style={{ marginBottom: '1.5rem' }}>
    <nav className="tabs-nav" role="tablist" aria-label="Settings sections">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          role="tab"
          aria-selected={activeTab === tab.id}
          aria-controls={`panel-${tab.id}`}
          id={`tab-${tab.id}`}
          onClick={() => setActiveTab(tab.id)}
          className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
        >
          <span className="tab-icon">{tab.icon}</span>
          <span className="tab-label">{tab.label}</span>
        </button>
      ))}
    </nav>
  </div>

  {/* Settings Content */}
  <div className="card" id={`panel-${activeTab}`} role="tabpanel" aria-labelledby={`tab-${activeTab}`}>
          {activeTab === 'profile' && (
            <div>
              <div className="section-header" style={{ marginBottom: '2rem' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--gray-900)' }}>Profile Information</h2>
                <button
                  onClick={() => isEditing ? handleProfileSave() : setIsEditing(true)}
                  className="btn btn-primary"
                >
                  {isEditing ? 'Save Changes' : 'Edit Profile'}
                </button>
              </div>

              {/* Profile Picture Section */}
              <div className="profile-hero">
                <div style={{ position: 'relative' }}>
                  <div style={{
                    width: '100px',
                    height: '100px',
                    borderRadius: '50%',
                    ...(profilePicturePreview || localProfile?.profilePicture ? {
                      backgroundImage: `url("${profilePicturePreview || localProfile?.profilePicture}")`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    } : {
                      backgroundColor: 'var(--gray-300)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '2rem',
                      color: '#9ca3af'
                    }),
                    border: '3px solid #ffffff',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}>
                    {!profilePicturePreview && !localProfile?.profilePicture && 'U'}
                  </div>
                  {isEditing && (
                    <label style={{
                      position: 'absolute',
                      bottom: '0',
                      right: '0',
                      background: '#3b82f6',
                      color: 'white',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                    }}>
                      +
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleProfilePictureChange}
                        style={{ display: 'none' }}
                      />
                    </label>
                  )}
                </div>
                <div>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--gray-900)' }}>{localProfile?.name || 'User'}</h3>
                  <p style={{ color: 'var(--gray-600)', marginBottom: '0.5rem' }}>{localProfile?.email || 'user@example.com'}</p>
                  <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>{localProfile?.occupation || 'Not specified'}</p>
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={localProfile?.name || ''}
                    onChange={(e) => setLocalProfile(prev => ({ ...prev, name: e.target.value }))}
                    disabled={!isEditing}
                  />
                </div>

                <div className="form-group" style={{ position: 'relative' }}>
                  <label className="form-label">Email Address</label>
                  <input
                    type="email"
                    className="form-input"
                    value={localProfile?.email || ''}
                    disabled={true}
                    title="Email cannot be changed as it's your account identifier"
                    style={{
                      backgroundColor: 'var(--bg-secondary)',
                      cursor: 'not-allowed',
                      opacity: 0.7
                    }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Phone Number</label>
                  <div className="phone-row">
                    <select
                      className="form-input"
                      value={localProfile?.countryCode || '+91'}
                      onChange={(e) => setLocalProfile(prev => ({ ...prev, countryCode: e.target.value }))}
                      disabled={!isEditing}
                    >
                      {countryCodes.map((country) => (
                        <option key={`${country.code}-${country.country}`} value={country.code}>
                          {country.country} {country.code}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      className={`form-input ${localProfile?.phone && !validatePhoneNumber(localProfile.phone, localProfile?.countryCode || '+91') ? 'error' : ''}`}
                      value={localProfile?.phone || ''}
                      onChange={handlePhoneNumberChange}
                      disabled={!isEditing}
                      placeholder={localProfile?.countryCode === '+91' ? '98765-432-10' : 'Enter phone number'}
                      maxLength={localProfile?.countryCode === '+91' ? 12 : 15}
                      style={{ flex: 1 }}
                    />
                  </div>
                  {localProfile?.phone && !validatePhoneNumber(localProfile.phone, localProfile?.countryCode || '+91') && (
                    <small className="error-text" style={{ marginTop: '0.25rem', color: 'var(--error-color, #e74c3c)' }}>
                      {localProfile?.countryCode === '+91' ? 'Please enter a valid 10-digit Indian mobile number (starting with 6-9)' : 'Please enter a valid phone number'}
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Date of Birth</label>
                  {isEditing ? (
                    <input
                      type="date"
                      className="form-input"
                      value={formatDateForInput(localProfile?.dateOfBirth)}
                      onChange={(e) => setLocalProfile(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                      max={new Date().toISOString().split('T')[0]}
                      min="1900-01-01"
                    />
                  ) : (
                    <input
                      type="text"
                      className="form-input"
                      value={formatDate(localProfile?.dateOfBirth)}
                      disabled
                      placeholder="DD/MM/YYYY"
                    />
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Occupation</label>
                  <input
                    type="text"
                    className="form-input"
                    value={localProfile?.occupation || ''}
                    onChange={(e) => setLocalProfile(prev => ({ ...prev, occupation: e.target.value }))}
                    disabled={!isEditing}
                    placeholder="Your profession"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Currency</label>
                  <select
                    className="form-input"
                    value={localProfile?.currency || 'USD'}
                    onChange={(e) => setLocalProfile(prev => ({ ...prev, currency: e.target.value }))}
                    disabled={!isEditing}
                  >
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="INR">INR - Indian Rupee</option>
                    <option value="CAD">CAD - Canadian Dollar</option>
                    <option value="AUD">AUD - Australian Dollar</option>
                  </select>
                </div>
              </div>

              <div className="form-group" style={{ marginTop: '1.5rem' }}>
                <label className="form-label">Bio</label>
                <textarea
                  className="form-input"
                  value={localProfile?.bio || ''}
                  onChange={(e) => setLocalProfile(prev => ({ ...prev, bio: e.target.value }))}
                  disabled={!isEditing}
                  rows="3"
                  placeholder="Tell us about yourself..."
                  style={{ resize: 'vertical', minHeight: '80px' }}
                />
              </div>
            </div>
          )}

          {activeTab === 'preferences' && (
            <div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '2rem', color: 'var(--gray-900)' }}>Preferences</h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                  <div>
                    <h3 style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '0.25rem' }}>Theme</h3>
                    <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>Choose your preferred theme</p>
                  </div>
                  <select
                    value={preferences.theme}
                    onChange={(e) => handlePreferenceChange('theme', e.target.value)}
                    className="form-input"
                    style={{ padding: '0.5rem', borderRadius: '6px', width: 'auto' }}
                  >
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                    <option value="system">System</option>
                  </select>
                </div>



                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                  <div>
                    <h3 style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '0.25rem' }}>Date Format</h3>
                    <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>Choose date display format</p>
                  </div>
                  <select
                    value={preferences.dateFormat}
                    onChange={(e) => handlePreferenceChange('dateFormat', e.target.value)}
                    className="form-input"
                    style={{ padding: '0.5rem', borderRadius: '6px', width: 'auto' }}
                  >
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  </select>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                  <div>
                    <h3 style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '0.25rem' }}>Currency Display</h3>
                    <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>How to display currency amounts</p>
                  </div>
                  <select
                    value={preferences.currencyDisplay}
                    onChange={(e) => handlePreferenceChange('currencyDisplay', e.target.value)}
                    className="form-input"
                    style={{ padding: '0.5rem', borderRadius: '6px', width: 'auto' }}
                  >
                    <option value="symbol">{(() => {
                      const currencyMap = { USD: '$', EUR: '€', GBP: '£', INR: '₹', CAD: 'C$', AUD: 'A$' };
                      return `${currencyMap[user?.currency] || '$'}1,234.56`;
                    })()}</option>
                    <option value="code">{user?.currency} 1,234.56</option>
                    <option value="name">1,234.56 {user?.currency === 'USD' ? 'US Dollar' : user?.currency === 'EUR' ? 'Euro' : user?.currency === 'GBP' ? 'British Pound' : user?.currency === 'INR' ? 'Indian Rupee' : user?.currency === 'CAD' ? 'Canadian Dollar' : user?.currency === 'AUD' ? 'Australian Dollar' : user?.currency}</option>
                  </select>
                </div>


              </div>
            </div>
          )}

          {activeTab === 'categories' && (
            <div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '2rem', color: 'var(--gray-900)' }}>Categories Management</h2>

              <div className="categories-grid">
                <div>
                  <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--success-green)' }}>Income Categories</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {categories.income.map((category, index) => (
                      <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: 'var(--gray-200)', borderRadius: '6px', border: '1px solid var(--gray-300)' }}>
                        <span>{category}</span>
                        <button onClick={() => removeCategory('income', category)} style={{ background: 'none', border: 'none', color: 'var(--danger-red)', cursor: 'pointer', fontSize: '1rem' }}>Delete</button>
                      </div>
                    ))}
                    
                    <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                      <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem', color: 'var(--success-green)' }}>Add New Income Category</h4>
                      
                      <input
                        type="text"
                        placeholder="Enter custom category name"
                        value={newIncomeCategory}
                        onChange={(e) => setNewIncomeCategory(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addIncomeCategory(newIncomeCategory)}
                        className="form-input"
                        style={{ width: '100%', fontSize: '0.875rem', marginBottom: '0.75rem' }}
                      />
                      
                      <div style={{ marginBottom: '0.75rem' }}>
                        <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.5rem' }}>Or select from predefined:</p>
                        <div className="grid-two-tight">
                          {predefinedIncomeCategories.filter(cat => !categories.income.includes(cat)).map((category) => (
                            <button
                              key={category}
                              onClick={() => addIncomeCategory(category)}
                              style={{ padding: '0.5rem', border: '1px solid var(--success-green)', background: 'var(--bg-primary)', borderRadius: '4px', fontSize: '0.75rem', cursor: 'pointer', color: 'var(--success-green)', transition: 'all 0.2s' }}
                              onMouseEnter={(e) => { 
                                e.target.style.background = '#f5f5f5'; 
                                e.target.style.color = '#333333'; 
                                e.target.style.borderColor = '#d4d4d4';
                              }}
                              onMouseLeave={(e) => { 
                                e.target.style.background = 'var(--bg-primary)'; 
                                e.target.style.color = 'var(--success-green)'; 
                                e.target.style.borderColor = 'var(--success-green)';
                              }}
                            >
                              {category}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      <button
                        onClick={() => addIncomeCategory(newIncomeCategory)}
                        disabled={!newIncomeCategory}
                        style={{ padding: '0.5rem 1rem', background: newIncomeCategory ? '#22c55e' : '#d4d4d4', color: newIncomeCategory ? '#ffffff' : '#333333', border: 'none', borderRadius: '4px', cursor: newIncomeCategory ? 'pointer' : 'not-allowed', fontSize: '0.75rem', fontWeight: '500' }}
                      >
                        Add Custom Category
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--danger-red)' }}>Expense Categories</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {categories.expense.map((category, index) => (
                      <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: 'var(--gray-200)', borderRadius: '6px', border: '1px solid var(--gray-300)' }}>
                        <span>{category}</span>
                        <button onClick={() => removeCategory('expense', category)} style={{ background: 'none', border: 'none', color: 'var(--danger-red)', cursor: 'pointer', fontSize: '1rem' }}>Delete</button>
                      </div>
                    ))}
                    
                    <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                      <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem', color: 'var(--danger-red)' }}>Add New Expense Category</h4>
                      
                      <input
                        type="text"
                        placeholder="Enter custom category name"
                        value={newExpenseCategory}
                        onChange={(e) => setNewExpenseCategory(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addExpenseCategory(newExpenseCategory)}
                        className="form-input"
                        style={{ width: '100%', fontSize: '0.875rem', marginBottom: '0.75rem' }}
                      />
                      
                      <div style={{ marginBottom: '0.75rem' }}>
                        <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.5rem' }}>Or select from predefined:</p>
                        <div className="grid-two-tight">
                          {predefinedExpenseCategories.filter(cat => !categories.expense.includes(cat)).map((category) => (
                            <button
                              key={category}
                              onClick={() => addExpenseCategory(category)}
                              style={{ padding: '0.5rem', border: '1px solid var(--danger-red)', background: 'var(--bg-primary)', borderRadius: '4px', fontSize: '0.75rem', cursor: 'pointer', color: 'var(--danger-red)', transition: 'all 0.2s' }}
                              onMouseEnter={(e) => { 
                                e.target.style.background = '#f5f5f5'; 
                                e.target.style.color = '#333333'; 
                                e.target.style.borderColor = '#d4d4d4';
                              }}
                              onMouseLeave={(e) => { 
                                e.target.style.background = 'var(--bg-primary)'; 
                                e.target.style.color = 'var(--danger-red)'; 
                                e.target.style.borderColor = 'var(--danger-red)';
                              }}
                            >
                              {category}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      <button
                        onClick={() => addExpenseCategory(newExpenseCategory)}
                        disabled={!newExpenseCategory}
                        style={{ padding: '0.5rem 1rem', background: newExpenseCategory ? '#ef4444' : '#d4d4d4', color: newExpenseCategory ? '#ffffff' : '#333333', border: 'none', borderRadius: '4px', cursor: newExpenseCategory ? 'pointer' : 'not-allowed', fontSize: '0.75rem', fontWeight: '500' }}
                      >
                        Add Custom Category
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '2rem', color: 'var(--gray-900)' }}>Security Settings</h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div className="form-group">
                  <label className="form-label">Current Password</label>
                  <input 
                    type="password" 
                    className="form-input" 
                    placeholder="Enter current password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <input 
                    type="password" 
                    className="form-input" 
                    placeholder="Enter new password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Confirm New Password</label>
                  <input 
                    type="password" 
                    className="form-input" 
                    placeholder="Confirm new password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  />
                </div>

                <button 
                  className="btn btn-primary" 
                  style={{ width: 'fit-content' }}
                  onClick={async () => {
                    if (passwordData.newPassword !== passwordData.confirmPassword) {
                      toast.error('Passwords do not match');
                      return;
                    }
                    try {
                      await changePassword({
                        currentPassword: passwordData.currentPassword,
                        newPassword: passwordData.newPassword
                      });
                      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                      toast.success('Password updated successfully');
                    } catch (error) {
                      console.error('Failed to change password:', error);
                      toast.error('Failed to update password');
                    }
                  }}
                  disabled={loading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                >
                  {loading ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'account' && (
            <div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '2rem', color: 'var(--gray-900)' }}>Account Management</h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                {/* Data Management */}
                <div style={{ padding: '1.5rem', background: 'var(--bg-secondary)', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--gray-900)' }}>Data Management</h3>
                  <div className="grid-two-tight">
                    <button onClick={() => setShowExportConfirm(true)} className="btn btn-primary" style={{ justifyContent: 'center' }}>
                      Export All Data
                    </button>
                    <label className="btn btn-secondary data-management-btn" style={{ justifyContent: 'center', cursor: 'pointer', border: '1px solid #d1d5db' }}>
                      Import Data
                      <input type="file" accept=".json,.csv" onChange={handleImportFile} style={{ display: 'none' }} />
                    </label>
                  </div>
                </div>

                {/* Account Statistics */}
                <div style={{ padding: '1.5rem', background: 'var(--bg-secondary)', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--gray-900)' }}>Account Statistics</h3>
                  <div className="grid-3" style={{ textAlign: 'center' }}>
                    <div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--trust-blue)' }}>{transactions.length}</div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>Total Transactions</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--success-green)' }}>{categories.income.length + categories.expense.length}</div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>Categories Used</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--warning-orange)' }}>{user?.createdAt ? Math.floor((new Date() - new Date(user.createdAt)) / (1000 * 60 * 60 * 24)) : 0} days</div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>Account Age</div>
                    </div>
                  </div>
                </div>

                {/* Account Actions */}
                <div style={{ padding: '1.5rem', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '12px' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--gray-900)' }}>Account Actions</h3>
                  <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '1rem' }}>
                    Permanently remove your account or all associated data.
                  </p>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="delete-account-btn"
                      style={{
                        padding: '0.75rem 1.5rem',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                      }}
                    >
                      Delete Account
                    </button>
                    <button
                      onClick={() => setShowClearDataConfirm(true)}
                      className="data-management-btn"
                      style={{
                        padding: '0.75rem 1.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                      }}
                    >
                      Clear All Data
                    </button>
                  </div>
                </div>
              </div>

              {/* Export Confirmation Modal */}
              {showExportConfirm && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000
                }}>
                  <div style={{
                    background: 'var(--white)',
                    padding: '2rem',
                    borderRadius: '12px',
                    maxWidth: '400px',
                    width: '90%'
                  }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: '#3b82f6' }}>
                      Export Data
                    </h3>
                    <p style={{ marginBottom: '1.5rem', color: 'var(--gray-500)' }}>
                      This will download all your data including:
                    </p>
                    <ul style={{ marginBottom: '1.5rem', color: 'var(--gray-500)', paddingLeft: '1.5rem' }}>
                      <li>Profile information</li>
                      <li>Preferences and settings</li>
                      <li>Custom categories</li>
                      <li>All transactions</li>
                    </ul>
                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => setShowExportConfirm(false)}
                        style={{
                          padding: '0.75rem 1.5rem',
                          background: 'var(--bg-secondary)',
                          color: '#000000',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={exportData}
                        style={{
                          padding: '0.75rem 1.5rem',
                          background: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        Export Data
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Import Confirmation Modal */}
              {showImportConfirm && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000
                }}>
                  <div style={{
                    background: 'var(--white)',
                    padding: '2rem',
                    borderRadius: '12px',
                    maxWidth: '400px',
                    width: '90%'
                  }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: '#f59e0b' }}>
                      Import Data
                    </h3>
                    <p style={{ marginBottom: '1.5rem', color: 'var(--gray-500)' }}>
                      Import data from JSON or CSV files:
                    </p>
                    <ul style={{ marginBottom: '1.5rem', color: 'var(--gray-500)', paddingLeft: '1.5rem' }}>
                      <li><strong>JSON:</strong> Profile, preferences, and categories</li>
                      <li><strong>CSV:</strong> Transaction data (Date, Description, Amount, Category)</li>
                    </ul>
                    <p style={{ marginBottom: '1.5rem', color: '#dc2626', fontSize: '0.875rem', fontWeight: '500' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span className="material-icons" style={{ fontSize: '16px', color: '#f59e0b' }}>warning</span>
                        This action cannot be undone. Consider exporting your current data first.
                      </span>
                    </p>
                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => {
                          setShowImportConfirm(false);
                          setPendingImportFile(null);
                        }}
                        style={{
                          padding: '0.75rem 1.5rem',
                          background: 'var(--bg-secondary)',
                          color: '#000000',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={importData}
                        style={{
                          padding: '0.75rem 1.5rem',
                          background: '#f59e0b',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        Import Data
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Delete Confirmation Modal */}
              {showDeleteConfirm && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000
                }}>
                  <div style={{
                    background: 'var(--white)',
                    padding: '2rem',
                    borderRadius: '12px',
                    maxWidth: '400px',
                    width: '90%'
                  }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--gray-900)' }}>
                      Delete Account
                    </h3>
                    <p style={{ marginBottom: '1.5rem', color: 'var(--gray-500)' }}>
                      Are you sure you want to delete your account? This action cannot be undone and will permanently delete:
                    </p>
                    <ul style={{ marginBottom: '1.5rem', color: 'var(--gray-500)', paddingLeft: '1.5rem' }}>
                      <li>All your transactions</li>
                      <li>All your categories</li>
                      <li>All your settings</li>
                      <li>Your profile information</li>
                    </ul>
                    <div style={{ marginBottom: '1.5rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--gray-900)' }}>
                        Enter your password:
                      </label>
                      <input
                        type="password"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        placeholder="Enter your password"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid var(--gray-300)',
                          borderRadius: '6px',
                          fontSize: '1rem',
                          marginBottom: '1rem'
                        }}
                      />
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--gray-900)' }}>
                        Type "DELETE" to confirm:
                      </label>
                      <input
                        type="text"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        placeholder="Type DELETE here"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid var(--gray-300)',
                          borderRadius: '6px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => {
                          setShowDeleteConfirm(false);
                          setDeleteConfirmText('');
                          setDeletePassword('');
                        }}
                        style={{
                          padding: '0.75rem 1.5rem',
                          background: 'var(--bg-secondary)',
                          color: '#000000',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={async () => {
                          if (deleteConfirmText === 'DELETE' && deletePassword) {
                            try {
                              const result = await deleteAccount(deletePassword);
                              if (result.success) {
                                toast.success('Account deleted successfully');
                                setShowDeleteConfirm(false);
                                setDeleteConfirmText('');
                                setDeletePassword('');
                                // Log out the user and redirect to login
                                logout();
                                window.location.href = '/login';
                              } else {
                                toast.error(result.error || 'Failed to delete account');
                              }
                            } catch (error) {
                              console.error('Delete account failed:', error);
                              toast.error('Failed to delete account');
                            }
                          }
                        }}
                        disabled={deleteConfirmText !== 'DELETE' || !deletePassword}
                        style={{
                          padding: '0.75rem 1.5rem',
                          background: deleteConfirmText === 'DELETE' ? 'var(--gray-900)' : 'var(--gray-400)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: deleteConfirmText === 'DELETE' ? 'pointer' : 'not-allowed'
                        }}
                      >
                        Delete Account
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Clear Data Confirmation Modal */}
              {showClearDataConfirm && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000
                }}>
                  <div style={{
                    background: 'var(--white)',
                    padding: '2rem',
                    borderRadius: '12px',
                    maxWidth: '400px',
                    width: '90%'
                  }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--gray-900)' }}>
                      Clear All Data
                    </h3>
                    <p style={{ marginBottom: '1.5rem', color: 'var(--gray-500)' }}>
                      This will permanently delete all your data:
                    </p>
                    <ul style={{ marginBottom: '1.5rem', color: 'var(--gray-500)', paddingLeft: '1.5rem' }}>
                      <li>All transactions</li>
                      <li>Custom categories</li>
                      <li>Settings and preferences</li>
                    </ul>
                    <div style={{ marginBottom: '1.5rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--gray-900)' }}>
                        Enter your password:
                      </label>
                      <input
                        type="password"
                        value={clearDataPassword}
                        onChange={(e) => setClearDataPassword(e.target.value)}
                        placeholder="Enter your password"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid var(--gray-300)',
                          borderRadius: '6px',
                          fontSize: '1rem',
                          marginBottom: '1rem'
                        }}
                      />
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--gray-900)' }}>
                        Type "CLEAR" to confirm:
                      </label>
                      <input
                        type="text"
                        value={clearDataConfirmText}
                        onChange={(e) => setClearDataConfirmText(e.target.value)}
                        placeholder="Type CLEAR here"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid var(--gray-300)',
                          borderRadius: '6px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => {
                          setShowClearDataConfirm(false);
                          setClearDataConfirmText('');
                          setClearDataPassword('');
                        }}
                        style={{
                          padding: '0.75rem 1.5rem',
                          background: 'var(--bg-secondary)',
                          color: '#000000',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={async () => {
                          if (clearDataConfirmText === 'CLEAR' && clearDataPassword) {
                            try {
                              const result = await clearData(clearDataPassword);
                              if (result.success) {
                                toast.success('All data cleared successfully');
                                setShowClearDataConfirm(false);
                                setClearDataConfirmText('');
                                setClearDataPassword('');
                                // Optionally refresh the page to show cleared data
                                window.location.reload();
                              } else {
                                toast.error(result.error || 'Failed to clear data');
                              }
                            } catch (error) {
                              console.error('Clear data failed:', error);
                              toast.error('Failed to clear data');
                            }
                          }
                        }}
                        disabled={clearDataConfirmText !== 'CLEAR' || !clearDataPassword}
                        style={{
                          padding: '0.75rem 1.5rem',
                          background: clearDataConfirmText === 'CLEAR' ? 'var(--gray-900)' : 'var(--gray-400)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: clearDataConfirmText === 'CLEAR' ? 'pointer' : 'not-allowed'
                        }}
                      >
                        Clear All Data
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
    </div>
  );
}

export default Settings;